import { reactive, watch, type UnwrapRef, onBeforeUnmount, ref, watchEffect, getCurrentScope } from 'vue'
import { sleep } from '../time'
import { debounce } from './debounce'

type AsyncFun<T = any> = () => Promise<T>
export type UseMemoAsyncItem<T = any> = {
  data: T
  retries: number
  loading: boolean
  error?: Error | unknown
  refresh: () => void;
  request: () => Promise<void>
}

type UseMemoAsyncOptions = {
  retry?: {
    count: number
    delay?: number
  }
  debounce?: {
    time: number
  };
  automatic?: boolean;
  deep?: boolean;
}

export const useMemoAsync = <T>(
  fun: AsyncFun<T>,
  deps?: any[],
  options: UseMemoAsyncOptions = {},
  initialValue: T | undefined = undefined
): UseMemoAsyncItem<typeof initialValue> => {
  const counter = ref<number>(0)
  const state = reactive<UseMemoAsyncItem<typeof initialValue>>({
    loading: true,
    retries: 0,
    refresh: () => {
      counter.value += 1
    },
    request: async () => {},
    data: initialValue
  })

  const fetchData = async () => {
    const op = async () => {
      const resp = await fun()
      state.data = resp as UnwrapRef<T>
    }

    state.loading = true

    try {
      await op()
      state.error = undefined
    } catch (e) {
      state.error = e
    }

    if (state.error && options && options.retry && options.retry.count > 0) {
      const delay = Math.max(options.retry.delay || 500, 500)
      for (let i = 0; i < options.retry.count; i++) {
        await sleep(delay)
        try {
          await op()
          state.error = undefined
        } catch (e) {
          state.error = e
          console.error(e)
        }
        if (!state.error) break
      }
    }

    state.loading = false
  }

  const fetchfun = options?.debounce ? debounce(fetchData, options.debounce.time) : fetchData;


  if (options.automatic) {
    watchEffect(async () => {
      await fetchfun();
    })
    const unwatchCounter = watch(counter, () => {
      fetchfun()
    })
    if (getCurrentScope()) {
      onBeforeUnmount(unwatchCounter)
    }
  } else {
    const unwatch = watch(
      [...(deps || []), counter],
      () => {
        fetchfun()
      },
      { immediate: true, deep: options?.deep }
    )
    if (getCurrentScope()) {
      onBeforeUnmount(unwatch)
    }
  }

  state.request = async () => {
    await fetchfun();
  }
  return state as UseMemoAsyncItem<typeof initialValue>
}
